import React, {
  useEffect,
  useState,
  useCallback,
  useRef
} from "react";

import axios from "axios";

import {
  useParams,
  useNavigate
} from "react-router-dom";


export default function TicketDetails() {

  const { id } = useParams();

  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);

  const [history, setHistory] = useState([]);

  const [messages, setMessages] = useState([]);

  const [newMsg, setNewMsg] = useState("");

  const [file, setFile] = useState(null);

  const [reopening, setReopening] = useState(false);

  const chatEndRef = useRef(null);


  // =========================================================
  // PRODUCTION API URL
  // =========================================================

  const API_BASE = "https://support.g6techcare.com";


  // =========================================================
  // DATE / TIME
  //
  // PRODUCTION SERVER:
  // UTC
  //
  // Example API value:
  //
  // 2026-10-05 10:00:00
  //
  // This value is treated as UTC.
  //
  // Then displayed as:
  //
  // IST          -> Asia/Kolkata
  // US Central   -> America/Chicago
  //
  // America/Chicago automatically handles:
  //
  // CST = UTC - 6
  // CDT = UTC - 5
  //
  // =========================================================

  const formatDateTime = (value) => {

    if (!value) {
      return null;
    }


    const raw = String(value).trim();


    // -------------------------------------------------------
    // Convert MySQL datetime format:
    //
    // 2026-10-05 10:00:00
    //
    // to:
    //
    // 2026-10-05T10:00:00
    // -------------------------------------------------------

    const normalized = raw.includes("T")
      ? raw
      : raw.replace(" ", "T");


    // -------------------------------------------------------
    // Check if API already contains timezone information.
    //
    // Examples:
    //
    // 2026-10-05T10:00:00Z
    //
    // 2026-10-05T10:00:00+00:00
    //
    // 2026-10-05T10:00:00-05:00
    // -------------------------------------------------------

    const hasTimezone =
      /[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized);


    // -------------------------------------------------------
    // If production API returns a timestamp without timezone,
    // treat it as UTC.
    // -------------------------------------------------------

    const utcValue = hasTimezone
      ? normalized
      : `${normalized}Z`;


    const parsedDate = new Date(utcValue);


    // -------------------------------------------------------
    // Invalid date protection
    // -------------------------------------------------------

    if (Number.isNaN(parsedDate.getTime())) {

      return {
        ist: raw,
        central: raw
      };

    }


    // =======================================================
    // IST
    // =======================================================

    const ist = new Intl.DateTimeFormat(
      "en-IN",
      {
        timeZone: "Asia/Kolkata",

        day: "2-digit",
        month: "short",
        year: "numeric",

        hour: "2-digit",
        minute: "2-digit",

        hour12: true
      }
    ).format(parsedDate);


    // =======================================================
    // US CENTRAL
    //
    // America/Chicago automatically handles CST/CDT.
    // =======================================================

    const central = new Intl.DateTimeFormat(
      "en-US",
      {
        timeZone: "America/Chicago",

        day: "2-digit",
        month: "short",
        year: "numeric",

        hour: "2-digit",
        minute: "2-digit",

        hour12: true,

        timeZoneName: "short"
      }
    ).format(parsedDate);


    return {
      ist,
      central
    };

  };


  // =========================================================
  // LOAD DATA
  // =========================================================

  const loadData = useCallback(async () => {

    try {

      const [
        t,
        m,
        h
      ] = await Promise.all([


        // Ticket
        axios.get(
          `${API_BASE}/api/ticket/${id}`
        ),


        // Chat
        axios.get(
          `${API_BASE}/api/tickets/chat/${id}`
        ),


        // Status History
        axios.get(
          `${API_BASE}/api/tickets/history/${id}`
        )

      ]);


      setTicket(t.data);


      setMessages(
        m.data || []
      );


      // Backend may return:
      //
      // [...]
      //
      // OR:
      //
      // { history: [...] }

      setHistory(
        Array.isArray(h.data)
          ? h.data
          : (h.data?.history || [])
      );


    } catch (error) {

      console.error(
        "Ticket loading error:",
        error
      );

    }

  }, [id]);


  useEffect(() => {

    loadData();

  }, [loadData]);


  // =========================================================
  // AUTO REFRESH
  // =========================================================

  useEffect(() => {

    const interval = setInterval(
      loadData,
      3000
    );


    return () => {

      clearInterval(interval);

    };

  }, [loadData]);


  // =========================================================
  // REOPEN TICKET
  // =========================================================

  const reopenTicket = async () => {

    if (reopening) {
      return;
    }


    const confirmed = window.confirm(
      "Are you sure you want to reopen this ticket?"
    );


    if (!confirmed) {
      return;
    }


    try {

      setReopening(true);


      const res = await axios.post(
        `${API_BASE}/api/ticket/${id}/reopen`
      );


      if (res.data?.status === "success") {

        alert(
          res.data.message ||
          "Ticket reopened successfully."
        );


        // Reload ticket + messages + history
        await loadData();


      } else {

        alert(
          res.data?.message ||
          "Unable to reopen ticket."
        );

      }


    } catch (error) {

      console.error(
        "Reopen ticket error:",
        error
      );


      alert(
        error.response?.data?.message ||
        "Unable to reopen ticket."
      );


    } finally {

      setReopening(false);

    }

  };


  // =========================================================
  // SEND MESSAGE
  // =========================================================

  const sendMessage = async () => {

    if (!newMsg && !file) {
      return;
    }


    const formData = new FormData();


    formData.append(
      "message",
      newMsg
    );


    if (file) {

      formData.append(
        "attachment",
        file
      );

    }


    const res = await axios.post(

      `${API_BASE}/api/tickets/chat/send/${id}`,

      formData

    );


    if (res.data.status === "sent") {

      setNewMsg("");

      setFile(null);

      loadData();

    }

  };


  // =========================================================
  // LOADING
  // =========================================================

  if (!ticket) {

    return (
      <div>
        Loading...
      </div>
    );

  }


  // =========================================================
  // CLOSED CHECK
  // =========================================================

  const isClosed =
    ticket.status?.toLowerCase() === "closed";


  // =========================================================
  // UI
  // =========================================================

  return (

    <div className="page">


      {/* =====================================================
          MODERN TICKET HEADER CARD
      ===================================================== */}

      <div className="ticket-card">


        {/* ===================================================
            TOP
        =================================================== */}

        <div className="ticket-topbar">


          <div>


            <div className="ticket-number">

              Ticket #{ticket.id}

            </div>


            <h2 className="ticket-subject">

              {ticket.subject}

            </h2>


            <div className="ticket-meta-row">


              <span className="meta-pill">

                {ticket.category || "General"}

              </span>


              <div
                className={`status-badge ${
                  ticket.status
                    ?.toLowerCase()
                    .replace(/\s/g, "-")
                }`}
              >

                {ticket.status}

              </div>


              <span className="priority-pill">

                {ticket.priority} Priority

              </span>


            </div>


          </div>


          {/* =================================================
              CLOSED TICKET
              SHOW REOPEN BUTTON
          ================================================= */}

          {isClosed ? (

            <button
              type="button"
              className="reopen-ticket-btn"
              onClick={reopenTicket}
              disabled={reopening}
            >

              {reopening
                ? "Reopening..."
                : "Reopen Ticket"}

            </button>

          ) : (

            <button
              type="button"
              className="edit-ticket-btn"
              onClick={() =>
                navigate(
                  `/ticket/${id}/edit`
                )
              }
            >

              Edit Ticket

            </button>

          )}


        </div>


        {/* ===================================================
            DESCRIPTION
        =================================================== */}

        <div className="ticket-section">


          <h4>
            Description
          </h4>


          <p className="ticket-description">

            {ticket.description}

          </p>


        </div>


        {/* ===================================================
            STATUS HISTORY
        =================================================== */}

        <div className="status-history-card">


          <h3>
            Status History
          </h3>


          {history.length === 0 ? (

            <p className="empty-history">

              No status history found

            </p>

          ) : (

            history.map((h, i) => (

              <div
                className="history-item"
                key={h.id || i}
              >


                {/* =========================================
                    STATUS
                ========================================= */}

                <div
                  className={`history-status ${
                    h.status
                      ? h.status
                          .toLowerCase()
                          .replace(/\s/g, "-")
                      : ""
                  }`}
                >

                  {h.status}

                </div>


                {/* =========================================
                    COMMENT
                ========================================= */}

                <div className="history-comment">


                  {(

                    h.status === "Pending" ||
                    h.status === "Dependency"

                  ) ? (

                    h.comment &&
                    h.comment !==
                      `Status changed to ${h.status}` ? (

                      <p>

                        {h.comment}

                      </p>

                    ) : (

                      <p className="auto-status-text">

                        No comment added

                      </p>

                    )

                  ) : (

                    h.comment ? (

                      <p>

                        {h.comment}

                      </p>

                    ) : (

                      <p className="auto-status-text">

                        Status changed to{" "}

                        <strong>

                          {h.status}

                        </strong>

                      </p>

                    )

                  )}


                </div>


                {/* =========================================
                    DATE / TIME
                ========================================= */}

                <small className="history-date">


                  {(() => {

                    const time =
                      formatDateTime(
                        h.updated_at
                      );


                    if (!time) {
                      return null;
                    }


                    return (

                      <div className="history-times">


                        <span className="time-pill ist">

                          <strong>
                            IST
                          </strong>{" "}

                          {time.ist}

                        </span>


                        <span className="time-pill cst">

                          <strong>
                            US Central
                          </strong>{" "}

                          {time.central}

                        </span>


                      </div>

                    );

                  })()}


                </small>


              </div>

            ))

          )}


        </div>


        {/* ===================================================
            INFO GRID
        =================================================== */}

        <div className="ticket-info-grid">


          <div className="info-box">

            <span className="label">
              Customer
            </span>

            <strong>

              {ticket.name || "N/A"}

            </strong>

          </div>


          <div className="info-box">

            <span className="label">
              Email
            </span>

            <strong>

              {ticket.email || "N/A"}

            </strong>

          </div>


          <div className="info-box">

            <span className="label">
              Mobile
            </span>

            <strong>

              {ticket.mobile || "N/A"}

            </strong>

          </div>


          <div className="info-box">

            <span className="label">
              Device
            </span>

            <strong>

              {ticket.device_type || "N/A"}

            </strong>

          </div>


          <div className="info-box">

            <span className="label">
              OS
            </span>

            <strong>

              {ticket.os || "N/A"}

            </strong>

          </div>


          {/* =================================================
              CREATED
          ================================================= */}

          <div className="info-box">

            <span className="label">
              Created
            </span>


            <strong>


              {(() => {

                const time =
                  formatDateTime(
                    ticket.created_at
                  );


                if (!time) {
                  return "N/A";
                }


                return (

                  <>

                    <div>

                      IST: {time.ist}

                    </div>


                    <div>

                      US Central: {time.central}

                    </div>

                  </>

                );

              })()}


            </strong>


          </div>


        </div>


      </div>


      {/* =====================================================
          CHAT
      ===================================================== */}

      <div className="chat-card">


        <h3>
          Conversation
        </h3>


        <div className="chat-box">


          {messages.map((m) => (


            <div
              key={m.id}
              className={`msg ${
                m.sender_type === "admin"
                  ? "user"
                  : "admin"
              }`}
            >


              <div className="bubble">


                <p>

                  {m.message}

                </p>


                {/* =========================================
                    ATTACHMENTS
                ========================================= */}

                {m.attachments?.length > 0 && (

                  <div className="thumb-grid">


                    {m.attachments.map((a, i) => (


                      <div
                        key={i}
                        className="thumb-card"
                        onClick={() =>
                          window.open(
                            `${API_BASE}/${a.file_path}`,
                            "_blank"
                          )
                        }
                      >


                        {[
                          "jpg",
                          "jpeg",
                          "png",
                          "gif",
                          "webp"
                        ].includes(
                          a.extension
                        ) ? (


                          <img
                            src={`${API_BASE}/${a.thumb_path}`}
                            alt={a.file_name}
                          />


                        ) : a.extension === "pdf" ? (


                          <div className="file-thumb">

                            📄 PDF

                          </div>


                        ) : (


                          <div className="file-thumb">

                            📎 FILE

                          </div>


                        )}


                      </div>


                    ))}


                  </div>

                )}


                {/* =========================================
                    MESSAGE TIME
                ========================================= */}

                <span className="msg-time">


                  {(() => {

                    const time =
                      formatDateTime(
                        m.created_at
                      );


                    if (!time) {
                      return null;
                    }


                    return (

                      <>

                        <div>

                          <strong>
                            IST:
                          </strong>{" "}

                          {time.ist}

                        </div>


                        <div>

                          <strong>
                            US Central:
                          </strong>{" "}

                          {time.central}

                        </div>

                      </>

                    );

                  })()}


                </span>


              </div>


            </div>

          ))}


          <div ref={chatEndRef} />


        </div>


        {/* ===================================================
            INPUT
        =================================================== */}

        {!isClosed && (

          <>


            <div className="input-bar">


              <div className="message-input-wrapper">


                <input
                  type="text"
                  value={newMsg}
                  onChange={(e) =>
                    setNewMsg(
                      e.target.value
                    )
                  }
                  placeholder="Type your reply..."
                  className="message-input"
                />


              </div>


              <label className="upload-btn">


                📎


                <input
                  type="file"
                  onChange={(e) =>
                    setFile(
                      e.target.files[0]
                    )
                  }
                  hidden
                />


              </label>


              <button
                type="button"
                className="send-btn"
                onClick={sendMessage}
              >

                Send

              </button>


            </div>


            {file && (

              <div className="selected-file">

                📄 {file.name}

              </div>

            )}


          </>

        )}


      </div>


    </div>

  );

}